import { useEffect } from "react";

import { WithStringValues } from "common/utils/types";

import Results from "client/common/components/results/Results";
import Body from "client/common/components/layouts/Body";
import { ProductListSource } from "client/analytics/types";

import { useFilterPanel, useProductSearchQuery } from "../hooks";
import { PRODUCT_FILTER_DEFINITIONS } from "../constants/filterDefinitions";
import { ProductSearchFilters, ProductSearchSearch, SelectedProductFilters } from "../types";
import ProductCardList from "./ProductCardList";
import ProductCardListHeader from "./ProductCardListHeader";
import FilterLayout from "./FilterLayout";
import FilterPanel from "./FilterPanel";
import { getProductFiltersFromSearch } from "../utils";

import styles from "../styles/ProductSearch.module.less";

type ProductSearchProps = {
  search: WithStringValues<ProductSearchSearch>;
  onUpdateSearch: (search: Partial<ProductSearchSearch>) => void;
};

const ProductSearch = (props: ProductSearchProps) => {
  const { search, onUpdateSearch } = props;

  const { data: products, isLoading, error, isRefetching } = useProductSearchQuery(search);

  const filters = getProductFiltersFromSearch(search);

  const panel = useFilterPanel<SelectedProductFilters, ProductSearchFilters>({
    definitions: PRODUCT_FILTER_DEFINITIONS,
    response: products,
    filters,
    onChange: onUpdateSearch,
  });
  const { filterOptions, showFilterPanel, hasSelectedFilters } = panel;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  if (isLoading) {
    return <Results.Loading description="Loading products" />;
  }

  if (error || !products) {
    return <Results.FetchError error={error} entityName="Product" />;
  }

  const renderEmpty = () => {
    if (hasSelectedFilters) {
      return <Results.None description="No results match the filters you have applied." />;
    }
    return <Results.None description={`Could not find any results for "${search.q}"`} />;
  };

  return (
    <Body>
      <FilterLayout panel={showFilterPanel && <FilterPanel.Rail {...panel} />}>
        <div className={styles.container}>
          <ProductCardListHeader
            paginator={products.paginator}
            isRefetching={isRefetching}
            entityName="result"
            filterOptions={filterOptions}
            filters={filters}
            onChange={onUpdateSearch}
            showFilters={!showFilterPanel}
          />
          <FilterPanel.AppliedRow {...panel} />
          {products.results.length ? (
            <ProductCardList
              results={products.results}
              listSource={ProductListSource.SearchResults}
              listContext={{
                searchTerm: search.q,
                numResults: products.paginator.count,
              }}
              paginator={products.paginator}
              onPageChange={(page) => onUpdateSearch({ page })}
              entityName="result"
              isNarrow={showFilterPanel}
            />
          ) : (
            renderEmpty()
          )}
        </div>
      </FilterLayout>
    </Body>
  );
};

export default ProductSearch;
